import { cn } from "@omni/utils/styles";
import { useState } from "react";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

type PasswordFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    autoComplete?: string;
    disabled?: boolean;
    ref?: React.Ref<HTMLInputElement>;
};

const PasswordField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    test,
    ref
}: PasswordFieldProps<T>) => {
    const { t } = useGranite();
    const { handleOnAutofill } = useAutofill<T>(name);

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showHint, setShowHint] = useState<boolean>(false);

    const toggleShowPassword = () => setShowPassword((current) => !current);

    const handleOnFocus = () => setShowHint(true);

    const handleOnBlur = (field: ControllerRenderProps<T, Path<T>>) => () => {
        field.onBlur();
        setShowPassword(false);
        setShowHint(false);
    };

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
        >
            {({ field, fieldState }) => (
                <div
                    className={cn("tw:relative", {
                        "is-invalid": fieldState.isInvalid,
                        "is-valid": fieldState.isValid
                    })}
                >
                    <Input
                        {...field}
                        id={name}
                        type={showPassword ? "text" : "password"}
                        placeholder={placeholder ? placeholder : ""}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        onFocus={handleOnFocus}
                        onBlur={handleOnBlur(field)}
                        disabled={disabled}
                        data-test={test}
                        ref={ref}
                        onAutofill={handleOnAutofill}
                    />
                    {showHint && (
                        <span
                            className={cn(
                                "tw:absolute tw:top-[0.4rem] tw:bottom-[0.4rem] tw:my-auto tw:mx-0 tw:bg-grey tw:right-[0.4rem] tw:text-xs",
                                "tw:py-1 tw:px-4 tw:rounded tw:hover:cursor-pointer",
                                fieldState.isValid && "tw:right-10"
                            )}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={toggleShowPassword}
                        >
                            {showPassword
                                ? t("form.input.password.hint.hide")
                                : t("form.input.password.hint.show")}
                        </span>
                    )}
                </div>
            )}
        </BaseField>
    );
};

export { PasswordField };
